import { keyframes } from "@emotion/react";
import { Box, Icon, Tooltip, Typography, useTheme } from "@outschool/backpack";
import {
  faCheckCircle,
  faDiamondExclamation,
  faQuestionCircle
} from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { Component } from "@outschool/ownership-areas";
import { adultThreadPathV2 } from "@outschool/routes";
import { CLASSROOM_BOX_SX } from "@outschool/ui-components-classroom";
import { Loading } from "@outschool/ui-legacy-component-library";
import { Redirect, useIsTablet, useNavigation } from "@outschool/ui-utils";
import React from "react";
import { useParams } from "react-router";

import loadingGif from "../../images/loading.gif";
import * as Env from "../../shared/Env";
import { sectionEditPath } from "../../shared/Routes";
import ErrorMessage from "../components/ErrorMessage";
import SimpleLayout from "../components/layouts/SimpleLayout";
import SectionManageToolbar from "../components/nav/SectionManageToolbar";
import NoteMakerInput from "../components/NoteMakerInput";
import TeacherNoteGenerater from "../components/TeacherNoteGenerater";
import useSectionMeetings from "../hooks/useSectionMeetings";
import { UserMode } from "../lib/UserMode";
import {
  useGenerateProgressReportMutation,
  useSendProgressReportMutation
} from "../queries/SectionMutations";
import NotFoundPage from "./NotFoundPage";

export default function SectionAiAssistantPage() {
  const isTablet = useIsTablet();
  const theme = useTheme();
  const { uid: sectionUid } = useParams<{ uid: string }>();
  const { t } = useTranslation("client\\routes\\SectionAiAssistantPage");
  const navigate = useNavigation();
  const generateProgressReport = useGenerateProgressReportMutation();
  const [sendProgressReport, { isSendingProgressReport }] =
    useSendProgressReportMutation();
  const [teacherNotes, setTeacherNotes] = React.useState<string | undefined>();
  const [isCreatingProgressReports, setIsCreatingProgressReports] =
    React.useState<boolean>(false);
  const [generatedLongNote, setGeneratedLongNote] = React.useState<
    string | undefined
  >();
  const [generatedShortNote, setGeneratedShortNote] = React.useState<
    string | undefined
  >();
  const [confimationError, setConfirmationError] = React.useState<
    string | undefined
  >();

  const { loading, error, section, enrollments } =
    useSectionMeetings(sectionUid);

  const currentLearner =
    enrollments === undefined
      ? { uid: "Student", name: "Student" }
      : {
          uid: enrollments[0].learner?.uid ?? "Student",
          name: enrollments[0].learner?.name ?? "Student"
        };

  const createProgressReport = async (teacherNotes: string | undefined) => {
    setIsCreatingProgressReports(true);
    if (!teacherNotes || teacherNotes.trim().length === 0) {
      setConfirmationError(t("Please enter notes before clicking button."));
      setIsCreatingProgressReports(false);
      return;
    }
    try {
      const [longResp, shortResp] = await Promise.all([
        generateProgressReport({
          sectionUid: sectionUid,
          notes: teacherNotes,
          reportLength: "FewParagraphs"
        }),
        generateProgressReport({
          sectionUid: sectionUid,
          notes: teacherNotes,
          reportLength: "OneParagraph"
        })
      ]);
      setGeneratedLongNote(longResp.data.generateProgressReport);
      setGeneratedShortNote(shortResp.data.generateProgressReport);
    } catch (err) {
      console.log(err);
      setConfirmationError(
        t(
          "Sorry, that didn’t work. We’ll look into it. You can reload the page or contact support@outschool.com."
        )
      );
      OsPlatform.captureError(err, {
        tags: { component: Component.TeacherTools }
      });
    }
    setIsCreatingProgressReports(false);
  };

  const sendProgressReportMessage = async (
    providedMessage: string | undefined,
    reportLength: string,
    generatedReport: boolean,
    changedGeneratedReport: boolean
  ) => {
    if (providedMessage === undefined || providedMessage.trim().length === 0) {
      setConfirmationError(
        t("Please provide notes before sending to parents.")
      );
      return;
    }
    const reportDetails = {
      originalNotes: teacherNotes ?? "no teacher note provided",
      reportLength: reportLength,
      didGenerateReport: generatedReport,
      didChangeGeneratedReport: changedGeneratedReport
    };

    try {
      const messageSent = await sendProgressReport({
        sectionUid: sectionUid,
        text: providedMessage,
        details: reportDetails
      });
      const messageRoute = adultThreadPathV2(
        UserMode.Educator,
        messageSent.data.sendProgressReport[0].thread_uid
      );
      navigate(messageRoute);
    } catch (err) {
      setConfirmationError(
        t(
          "Sorry, that didn’t work. We’ll look into it. You can reload the page or contact support@outschool.com."
        )
      );
      OsPlatform.captureError(err, {
        tags: { component: Component.TeacherTools }
      });
    }
  };

  if (section && section.activity && !section.activity.hasTeacherSchedule) {
    return (
      <SimpleLayout userMode={UserMode.Educator}>
        <Redirect to={sectionEditPath(section)} />
      </SimpleLayout>
    );
  }

  if (loading && !section) {
    return (
      <SimpleLayout userMode={UserMode.Educator}>
        <Loading />
      </SimpleLayout>
    );
  }

  if (error) {
    return (
      <SimpleLayout userMode={UserMode.Educator}>
        <ErrorMessage
          value={error}
          showStatusPageLink={Env.IS_READ_ONLY_MODE}
        />
      </SimpleLayout>
    );
  }

  if (!section) {
    return <NotFoundPage userMode={UserMode.Educator} />;
  }

  return (
    <SimpleLayout
      userMode={UserMode.Educator}
      sx={{ backgroundColor: theme.palette.neutral[50] }}
    >
      <SectionManageToolbar
        activity={section.activity}
        section={section}
        learner={currentLearner}
      />
      <Box sx={{ ...CLASSROOM_BOX_SX }}>
        <Box
          flex
          sx={{
            flexDirection: isTablet ? "column" : "row",
            mb: "1rem"
          }}
        >
          <NoteMakerInput
            learner={currentLearner}
            createProgressReport={createProgressReport}
            teacherNotes={teacherNotes}
            setTeacherNotes={setTeacherNotes}
            confimationError={confimationError}
            setConfirmationError={setConfirmationError}
            isGeneratingProgressReport={isCreatingProgressReports}
            sendProgressReportMessage={sendProgressReportMessage}
            isSendingProgressReport={isSendingProgressReport}
            lastProgressReportDate={section.latestProgressReportDate}
            isTablet={isTablet}
            boxSx={{ padding: "2rem" }}
          />
          <Box
            flex
            sx={{
              flexDirection: "column",
              marginTop: "1em",
              width: isTablet ? "100%" : "20%"
            }}
          >
            <Box
              flex
              sx={{
                flexDirection: "row"
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  alignSelf: "start",
                  marginY: "0.5em",
                  marginX: "0.6em"
                }}
              >
                {t("Do & Don’t")}
              </Typography>
              <Tooltip
                placement="top"
                title={t(
                  "Why These Guidelines? Our AI Teaching Assistant was the result of notable progress in AI technology. Our top priority is to protect both learners and your privacy while improving the learning experience."
                )}
              >
                <Icon
                  icon={faQuestionCircle}
                  sx={{
                    marginTop: "1.2em"
                  }}
                />
              </Tooltip>
            </Box>
            <Box
              flex
              sx={{
                flexDirection: isTablet ? "row" : "column"
              }}
            >
              <Box
                flex
                sx={{
                  border: "solid 1px #4CAF50",
                  backgroundColor: "#EEF7EE",
                  flexDirection: "row",
                  borderRadius: "8px",
                  marginLeft: "2rem",
                  marginBottom: "1rem",
                  width: "100%"
                }}
              >
                <Icon
                  icon={faCheckCircle}
                  sx={{ marginY: "1.2em", marginX: "0.8em", color: "#4CAF50" }}
                />
                <Box
                  flex
                  sx={{
                    flexDirection: "column",
                    margin: "0.3em"
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      alignSelf: "start",
                      marginY: "0.5em",
                      marginRight: "0.8em",
                      color: "#225624"
                    }}
                  >
                    {t("Respect Confidentiality")}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      alignSelf: "start",
                      marginRight: "0.7em",
                      color: "#225624"
                    }}
                  >
                    {t(
                      "Share feedback only with the learner’s parents or guardians."
                    )}
                  </Typography>
                </Box>
              </Box>
              <Box
                flex
                sx={{
                  border: "solid 1px #D32F2F",
                  backgroundColor: "#FFF2EE",
                  flexDirection: "row",
                  borderRadius: "8px",
                  marginLeft: "2rem",
                  marginBottom: "1rem",
                  width: "100%"
                }}
              >
                <Icon
                  icon={faDiamondExclamation}
                  sx={{ marginY: "1.2em", marginX: "0.8em", color: "#D32F2F" }}
                />
                <Box
                  flex
                  sx={{
                    flexDirection: "column",
                    margin: "0.3em"
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      alignSelf: "start",
                      marginY: "0.5em",
                      marginRight: "0.8em",
                      color: "#8F1104"
                    }}
                  >
                    {t("No Personal Information")}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      alignSelf: "start",
                      marginRight: "0.7em",
                      color: "#8F1104"
                    }}
                  >
                    {t(
                      "Avoid any personal details about learners (e.g., names, addresses, location, race, gender identity, religion, or health/medical details)."
                    )}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
        {isCreatingProgressReports && (
          <Box
            flex
            sx={{
              backgroundColor: theme.palette.common.white,
              borderBottom: "solid 1px #DDDFE3",
              flexDirection: "column",
              boxShadow: "3px 3px 5px 2px rgba(23, 25, 28, 0.1)",
              borderRadius: "8px",
              paddingBottom: "16px",
              paddingX: "2rem",
              paddingY: "1rem",
              minHeight: "8em",
              width: isTablet ? "100%" : "83%"
            }}
          >
            <NoteResponseLoading />
          </Box>
        )}
        {!isCreatingProgressReports && generatedLongNote && (
          <Box
            flex
            sx={{
              backgroundColor: theme.palette.common.white,
              borderBottom: "solid 1px #DDDFE3",
              flexDirection: "column",
              boxShadow: "3px 3px 5px 2px rgba(23, 25, 28, 0.1)",
              borderRadius: "8px",
              paddingBottom: "16px",
              paddingX: "2rem",
              paddingY: "1rem",
              minHeight: "8em",
              width: isTablet ? "100%" : "82%"
            }}
          >
            <TeacherNoteGenerater
              sectionUid={sectionUid}
              teacherNotes={teacherNotes}
              generatedLongNote={generatedLongNote}
              generatedShortNote={generatedShortNote}
              sendProgressReportMessage={sendProgressReportMessage}
              isSendingProgressReport={isSendingProgressReport}
              isGeneratingProgressReport={isCreatingProgressReports}
              isTablet={isTablet}
            />
          </Box>
        )}
      </Box>
    </SimpleLayout>
  );
}

export function NoteResponseLoading() {
  const { t } = useTranslation("client\\routes\\SectionAiAssistantPage");
  const loadingText = [
    t("Stirring up the cauldron..."),
    t("Sprinkling the fairy dust..."),
    t("Almost there...")
  ];
  const [loadingTextIdx, setLoadingTextIdx] = React.useState(0);

  const slideInAnimation = keyframes`
    0% {
      transform: translateX(-50%);
      opacity: 0;
    }
    15% {
      transform: translateX(0%);
      opacity: 1;
    }
    75% {
      transform: translateX(0%);
      opacity: 1;
    }
    100% {
      transform: translateX(50%);
      opacity: 0;
    }
  `;

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setLoadingTextIdx((loadingTextIdx + 1) % loadingText.length);
    }, 2000);
    return () => clearTimeout(timer);
  }, [loadingTextIdx, loadingText.length]);
  const currentText = loadingText[loadingTextIdx];

  return (
    <>
      <Box
        flex
        sx={{
          justifyContent: "space-around"
        }}
      >
        <img alt={t("Loading")} src={loadingGif} width={"138px"} />
      </Box>
      <Box
        flex
        sx={{
          justifyContent: "space-around"
        }}
      >
        <Typography
          variant="h6"
          sx={{
            animation: `${slideInAnimation} 2s`,
            animationIterationCount: "infinite",
            paddingBottom: "1em"
          }}
        >
          {currentText}
        </Typography>
      </Box>
    </>
  );
}
